<template>
	<div click.stop>
		<div class="modal is-active">
			<div class="modal-background" @click="cancel"></div>
			<div class="modal-card">
				<header class="modal-card-head">
					<p class="modal-card-title">
						{{ editFlaw.id ? $t('flaw_modal.update') : $t('flaw_modal.new') }} {{ $t('flaw_modal.flaw') }}
					</p>
					<button class="delete" aria-label="close" @click="cancel"></button>
				</header>
				<section class="modal-card-body no-scroll">
					<div class="columns is-mobile">
						<div class="field column">
							<label class="label">{{ $t('flaw_modal.inventory') }}</label>
							<div class="control">
								<input
									type="text"
									v-model="mapItemName"
									class="input"
									disabled
								/>
							</div>
						</div>
						<div class="field column">
							<label class="label">{{ $t('flaw_modal.flaw') }}</label>
							<div class="control">
								<input
									type="text"
									v-model="flaw.name"
									class="input"
									:placeholder="$t('flaw_modal.flaw_placeholder')"
									disabled
								/>
							</div>
						</div>
					</div>

					<div class="columns is-mobile">
						<div class="field column">
							<label class="label">{{ $t('flaw_modal.floor') }}</label>
							<div class="control">
								<input
									type="text"
									v-model="floorName"
									class="input"
									:placeholder="$t('flaw_modal.floor_placeholder')"
									disabled
								/>
							</div>
						</div>
						<div class="field column narrow">
							<label class="label">{{ $t('flaw_modal.position_number') }}</label>
							<div class="control">
								<input
									type="text"
									v-model="positionNumber"
									class="input"
									:placeholder="$t('flaw_modal.position_number_placeholder')"
									disabled
								/>
							</div>
						</div>
					</div>

					<div class="columns is-mobile">
						<div class="field column">
							<label class="label">{{ $t('flaw_modal.description') }}</label>
							<div class="control">
								<input
									:disabled="editFlaw.id"
									type="email"
									v-model="flaw.description"
									class="input"
									:placeholder="$t('flaw_modal.description_placeholder')"
								/>
							</div>
						</div>
					</div>
					<div class="columns is-mobile">
						<div class="field column" style="margin-top: 12px">
							<label class="label">{{ $t('flaw_modal.responsible') }}</label>
							<div class="control">
								<input
									:disabled="editFlaw.id"
									type="email"
									v-model="responsible"
									class="input"
									:placeholder="$t('flaw_modal.responsible_placeholder')"
								/>
							</div>
						</div>
						<div class="field column" style="margin-top: 12px">
							<label class="label">{{ $t('flaw_modal.performer') }}</label>
							<div class="control">
								<input
									:disabled="editFlaw.id"
									type="email"
									v-model="flaw.performer"
									class="input"
									:placeholder="$t('flaw_modal.performer_placeholder')"
								/>
							</div>
						</div>
					</div>
					<div class="columns is-mobile">
						<div class="field column">
							<label class="label">{{ $t('flaw_modal.measure') }}</label>
							<div class="control">
								<input
									type="text"
									v-model="editFlaw.measure"
									class="input"
									:readonly="!isOther"
								/>
							</div>
						</div>
						<div class="field column">
							<label class="label">{{ $t('flaw_modal.created') }}</label>
							<div class="control">
								<input type="email" v-model="createdDate" class="input" />
							</div>
						</div>
					</div>
					<img
						v-if="imgSrc"
						class="image-preview"
						:src="imgSrc"
						@click="toggleImage"
					/>
				</section>
				<footer class="modal-card-foot">
					<div class="column">
						<button
							class="button is-dark"
							:class="isLoading ? 'is-loading' : ''"
							:disabled="disableSave"
							@click="save"
							v-if="!editFlaw.done"
						>
							<span class="icon is-info"><i class="fas fa-save"/></span
							><span>{{ $t('flaw_modal.save') }}</span>
						</button>
						<button
							class="button is-dark"
							:class="isLoading ? 'is-loading' : ''"
							:disabled="disableSave"
							@click="fix"
							v-if="!editFlaw.done"
						>
							<span class="icon is-info"><i class="fas fa-check"/></span
							><span>{{ $t('flaw_modal.fix') }}</span>
						</button>
						<button
							v-if="mapItem && mapFile"
							class="button is-dark"
							:class="isLoading ? 'is-loading' : ''"
							:disabled="disableSave"
							@click="toggleModal"
						>
							<span class="icon is-info"><i class="fas fa-check"/></span
							><span>{{ $t('flaw_modal.show_on_map') }}</span>
						</button>
					</div>
				</footer>
			</div>
			<div class="image-display" v-if="showImage" @click="toggleImage">
				<img v-if="imgSrc" :src="imgSrc" />
			</div>
		</div>
		<FlawOnMap
			v-if="showMapModal"
			:flaw="mapItem"
			:mapImg="mapFile"
			@cancel="showMapModal = !showMapModal"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import FlawOnMap from './FlawOnMap.vue';

export default {
	components: {
		FlawOnMap
	},
	props: {
		editFlaw: Object
	},
	data() {
		return {
			flaw: {
				name: '',
				flaw: '',
				email: '',
				phoneNumber: ''
			},
			isLoading: false,
			responsible: '',
			imgSrc: '',
			showImage: false,
			showMapModal: false,
			mapFile: null
		};
	},
	watch: {
		errorMessage: function() {
			this.isLoading = false;
		}
	},
	computed: {
		...mapGetters('roleStore', ['role']),
		...mapState('fileStore', ['file']),
		...mapState('mapStore', ['map']),
		...mapGetters('mapItemStore', ['mapItem']),
		...mapGetters('roundHistoryInfoStore', ['roundHistoryInfo']),
		...mapGetters('user', ['getUser']),
		disableSave() {
			return false;
		},
		createdDate() {
			return this.moment(this.flaw.created).format('YYYY-MM-DD HH:mm');
		},
		doneDate() {
			return this.flaw.date
				? this.moment(this.flaw.date).format('YYYY-MM-DD HH:mm')
				: '';
		},
		mapItemName() {
			return this.editFlaw.mapItemName || '-';
		},
		floorName() {
			if (this.mapItem.id && this.mapItem.map) {
				return `${this.mapItem.map.floor.buildingName} - ${this.mapItem.map.floor.name}`;
			}

			if (this.roundHistoryInfo.id) {
				return `${this.roundHistoryInfo.buildingName} - ${this.roundHistoryInfo.floorName}`;
			}

			return '';
		},
		positionNumber() {
			return this.mapItem.positionNumber || '-';
		},
		isOther() {
			const key = this.editFlaw?.flawType?.translationKey || '';
			return key.toLowerCase().endsWith('other');
		}
	},
	methods: {
		...mapActions('roleStore', ['getRole']),
		...mapActions('fileStore', ['getFile']),
		...mapActions('mapItemStore', ['getMapItem', 'setMapItem']),
		...mapActions('roundHistoryInfoStore', [
			'getRoundHistoryInfo',
			'setRoundHistoryInfo'
		]),
		...mapActions('mapStore', ['getMap']),
		save() {
			this.$emit('save', this.editFlaw);
		},
		fix() {
			let date = new Date();
			date.setHours(date.getHours() - date.getTimezoneOffset() / 60);
			this.editFlaw.done = date;
			this.editFlaw.doneBy = this.getUser.email;
			this.save();
		},
		cancel() {
			this.$emit('cancel');
		},
		toggleImage() {
			this.showImage = !this.showImage;
		},
		toggleModal() {
			this.showMapModal = !this.showMapModal;
		}
	},
	async created() {
		if (this.editFlaw.id) {
			Object.assign(this.flaw, this.editFlaw);
		}

		this.getRole(this.editFlaw.responsible).then(() => {
			this.responsible = this.role.name || this.editFlaw.responsible;
		});

		if (this.flaw.mapItemId) {
			await this.getMapItem(this.flaw.mapItemId);
			await this.getMap(this.mapItem.map.id);

			if (this.map.file && this.map.file.id) {
				this.mapFile = await this.getFile(this.map.file.id);
			}
		} else {
			this.setMapItem({});
		}

		if (this.flaw.roundHistoryInfoId) {
			await this.getRoundHistoryInfo(this.flaw.roundHistoryInfoId);

			if (Object.keys(this.mapItem).length === 0) {
				await this.getMap(this.roundHistoryInfo.mapId);

				if (this.map.file && this.map.file.id) {
					this.mapFile = await this.getFile(this.map.file.id);
					this.setMapItem({
						...this.flaw,
						...{
							map: {
								floor: {
									name: this.roundHistoryInfo.floorName,
									buildingName: this.roundHistoryInfo.buildingName
								},
								name: this.map.name
							},
							fireProtection: {
								inventoryType: 'misc'
							},
							positionX: this.flaw.flawPositionX,
							positionY: this.flaw.flawPositionY
						}
					});
				}
			}
		} else {
			this.setRoundHistoryInfo({});
		}

		if (this.editFlaw.fileId) {
			this.getFile(this.editFlaw.fileId).then(() => {
				this.imgSrc = this.file;
			});
		}
	}
};
</script>

<style lang="scss">
.multiselect.panel-select.flaw .multiselect__tags {
  white-space: normal;
}
</style>

<style lang="scss" scoped>
.image-preview {
  margin-top: 1rem;
  max-width: 5rem;
  background: white;
  cursor: pointer;
}

.image-display {
  position: absolute;
  z-index: 10;
  background: rgba(0, 0, 0, 0.486);
  height: 80%;
  width: 80%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    height: 80%;
  }
}
.field.column.narrow {
  max-width: 80px;
}
input.input {
  height: 36px;
}
.modal-card-body.no-scroll {
  padding: 0 10px;
}
</style>
